import { queryClient } from "@/client";
import { ErrorResponseDto } from "@advanced-sales-technologies/backend-client/models/ErrorResponseDto";
import { GetListsResultDto } from "@advanced-sales-technologies/backend-client/models/GetListsResultDto";
import { InfiniteData, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiOutlineCheckCircle } from "react-icons/hi2";

import { InfiniteListsParams } from "@/hooks/api/list/useInfiniteLists";
import useSalesyApiClient from "@/hooks/useSalesyApiClient";
import { useToast } from "@/hooks/useToast";

export type ListUpdateBody = {
  id: string;
  name: string;
};

type UpdateListOptions = {
  listFilter: InfiniteListsParams;
  displayToast?: boolean;
};

export const useUpdateList = ({ listFilter, displayToast = true }: UpdateListOptions) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { apiClient } = useSalesyApiClient();

  const mutation = useMutation({
    mutationFn: (requestBody: ListUpdateBody) =>
      apiClient.lists.update({ id: requestBody.id, requestBody: { name: requestBody.name } }),
    onSuccess: (_, variables) => {
      // Update the query data by removing the deleted items
      queryClient.setQueryData<InfiniteData<GetListsResultDto>>(["lists", listFilter], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((item) => (item.id === variables.id ? { ...item, name: variables.name } : item)),
          })),
        };
      });

      // Show a success toast notification
      if (displayToast) {
        showToast({
          featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
          heading: t("list.update.success"),
        });
      }
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onError: ({ body }: ErrorResponseDto) => {
      if (body?.status_code === 409) {
        if (body?.internal_status_code === 40902) {
          showToast({
            featuredIcon: { icon: HiOutlineCheckCircle, color: "error" },
            heading: t("list.update.error.duplicate"),
          });
        }
      }
    },
  });

  const { mutate, ...mutationResult } = mutation;

  return {
    ...mutationResult,
    updateList: mutate,
  };
};
